/**
 * Replaces URL path parameters (e.g `:userId`) with template literal string to
 * maintain the URL type e.g `/presentation/api/v2/users/:userId/` becomes
 * `/presentation/api/v2/users/${string}/` - this closer resembles the URL type
 * once the path parameters have been encoded at runtime.
 */
export type GenericPath<T> =
  T extends `${infer TStart}:${string}/${infer TRest}`
    ? `${TStart}${string}/${GenericPath<TRest>}`
    : T;

/**
 * Creates union of path parameters from given string
 * E.g /presentation/api/v2/users/:userId/product/:productId/
 * becomes type userId | productId
 */
type PathParamsUnion<T> = Exclude<
  T extends `${string}:${infer Param}/${infer Rest}`
    ? Param | PathParamsUnion<Rest>
    : never,
  ''
>;

/**
 * Constructs object type from path parameters union
 * E.g userId | productId becomes { userId: string; productId: string; }
 */
export type ExtractPathParams<T> =
  PathParamsUnion<T> extends never
    ? Record<string, never>
    : {
        [Path in PathParamsUnion<T>]: string;
      };

/**
 * Parameterise a given URL with given params (type derived from url)
 * @param url: An endpoint with path parameters
 * @param params: Object containing required path params for given {url}
 * @returns URL with provided path params replaced
 */
export function replacePathParams<T extends string>(
  url: `${string}${T}`,
  params: ExtractPathParams<T>
): `${string}${GenericPath<T>}` {
  if (!params) {
    return url as `${string}${GenericPath<T>}`;
  }
  let newUrl: string = url;
  for (const [key, value] of Object.entries(params)) {
    newUrl = newUrl.replace(`:${key}`, String(value));
  }
  return newUrl as `${string}${GenericPath<T>}`;
}
